import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft,
  RefreshCw,
  Users,
  CheckCircle,
  Clock,
  TrendingUp,
  Activity,
  BarChart3,
  PieChart,
  Zap,
  AlertCircle,
  XCircle
} from 'lucide-react';

// Simple Bar Chart Component
const SimpleBarChart = ({ data, maxHeight = 200, barColor = '#10b981' }) => {
  const maxValue = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="flex items-end justify-between gap-1 h-[200px] px-2">
      {data.map((item, idx) => (
        <div key={idx} className="flex flex-col items-center flex-1 min-w-0">
          <div 
            className="w-full rounded-t transition-all duration-300 hover:opacity-80"
            style={{ 
              height: `${(item.count / maxValue) * maxHeight}px`,
              backgroundColor: barColor,
              minHeight: item.count > 0 ? '4px' : '0'
            }}
            title={`${item.hour}: ${item.count} scans`}
          />
          <span className="text-[10px] text-gray-400 mt-1 truncate w-full text-center">
            {item.hour.split(':')[0]}
          </span>
        </div>
      ))}
    </div>
  );
};

// Horizontal Bar Chart for Scanner Rankings
const HorizontalBarChart = ({ data, maxValue }) => {
  const max = maxValue || Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="space-y-3">
      {data.map((item, idx) => (
        <div key={idx} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="font-medium truncate flex-1">{item.dashboard_name}</span>
            <span className="text-gray-500 ml-2">{item.count}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${(item.count / max) * 100}%`,
                backgroundColor: idx === 0 ? '#10b981' : idx === 1 ? '#3b82f6' : idx === 2 ? '#8b5cf6' : '#6b7280'
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Donut Chart for Status Breakdown
const DonutChart = ({ data }) => {
  const total = data.success + data.duplicate + data.invalid + data.unauthorized + data.limit_exceeded;
  if (total === 0) return <div className="text-center text-gray-400 py-8">No data</div>;
  
  const segments = [
    { name: 'Success', value: data.success, color: '#10b981' },
    { name: 'Duplicate', value: data.duplicate, color: '#f59e0b' },
    { name: 'Invalid', value: data.invalid, color: '#ef4444' },
    { name: 'Unauthorized', value: data.unauthorized, color: '#f97316' },
    { name: 'Limit Exceeded', value: data.limit_exceeded, color: '#8b5cf6' }
  ].filter(s => s.value > 0);
  
  let currentAngle = 0;
  const radius = 60;
  const strokeWidth = 20;
  
  return (
    <div className="flex items-center justify-center gap-6">
      <svg width="160" height="160" viewBox="0 0 160 160">
        <circle cx="80" cy="80" r={radius} fill="none" stroke="#f3f4f6" strokeWidth={strokeWidth} />
        {segments.map((segment, idx) => {
          const angle = (segment.value / total) * 360;
          const startAngle = currentAngle;
          currentAngle += angle;
          
          const largeArc = angle > 180 ? 1 : 0;
          const startRad = (startAngle - 90) * Math.PI / 180;
          const endRad = (currentAngle - 90) * Math.PI / 180;
          
          const x1 = 80 + radius * Math.cos(startRad);
          const y1 = 80 + radius * Math.sin(startRad);
          const x2 = 80 + radius * Math.cos(endRad);
          const y2 = 80 + radius * Math.sin(endRad);
          
          return (
            <path
              key={idx}
              d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`}
              fill="none"
              stroke={segment.color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
          );
        })}
        <text x="80" y="75" textAnchor="middle" className="text-2xl font-bold fill-gray-900">
          {total}
        </text>
        <text x="80" y="95" textAnchor="middle" className="text-xs fill-gray-500">
          Total Scans
        </text>
      </svg>
      <div className="space-y-2">
        {segments.map((segment, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: segment.color }} />
            <span className="text-gray-600">{segment.name}</span>
            <span className="font-medium ml-auto">{segment.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Live Feed Item
const LiveFeedItem = ({ scan }) => {
  const statusColors = {
    success: 'bg-green-100 text-green-700 border-green-200',
    duplicate: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    invalid: 'bg-red-100 text-red-700 border-red-200',
    unauthorized: 'bg-orange-100 text-orange-700 border-orange-200'
  };
  
  const StatusIcon = {
    success: CheckCircle,
    duplicate: AlertCircle,
    invalid: XCircle,
    unauthorized: AlertCircle
  }[scan.scan_status] || Activity;
  
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${statusColors[scan.scan_status] || 'bg-gray-50'}`}>
      <StatusIcon className="h-4 w-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{scan.attendee_name || 'Unknown'}</p>
        <p className="text-xs opacity-75">{scan.dashboard_name} • {scan.ticket_id}</p>
      </div>
      <span className="text-xs opacity-75 whitespace-nowrap">
        {scan.scanned_at?.slice(11, 19)}
      </span>
    </div>
  );
};

const ScannerAnalytics = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Analytics data
  const [realtimeStats, setRealtimeStats] = useState(null);
  const [scansPerHour, setScansPerHour] = useState([]);
  const [scansByScanner, setScansByScanner] = useState([]);
  const [statusBreakdown, setStatusBreakdown] = useState(null);
  const [categoryRates, setCategoryRates] = useState([]);
  const [recentScans, setRecentScans] = useState([]);
  
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchAllData = useCallback(async (showToast = false) => {
    try {
      setRefreshing(true);
      
      const [
        eventRes,
        realtimeRes,
        hourlyRes,
        scannerRes,
        statusRes,
        categoryRes,
        recentRes
      ] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/events/${eventId}/analytics/realtime-stats`),
        api.get(`/events/${eventId}/analytics/scans-per-hour`, { params: { date: selectedDate } }),
        api.get(`/events/${eventId}/analytics/scans-by-scanner`),
        api.get(`/events/${eventId}/analytics/scan-status-breakdown`),
        api.get(`/events/${eventId}/analytics/category-checkin-rate`),
        api.get(`/events/${eventId}/analytics/recent-scans`, { params: { limit: 8 } })
      ]);
      
      setEvent(eventRes.data);
      setRealtimeStats(realtimeRes.data);
      setScansPerHour(hourlyRes.data.data || []);
      setScansByScanner(scannerRes.data.data || []);
      setStatusBreakdown(statusRes.data);
      setCategoryRates(categoryRes.data.data || []);
      setRecentScans(recentRes.data.scans || []);
      
      if (showToast) toast.success('Data refreshed');
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      if (showToast) toast.error('Failed to refresh data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [eventId, selectedDate]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchAllData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchAllData]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/events/${eventId}`)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-emerald-500" />
            Scanner Analytics
          </h1>
          <p className="text-gray-500">{event?.event_name}</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh
          </label>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fetchAllData(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Real-time Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Users className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-700">
                  {realtimeStats?.unique_checked_in || 0}
                  <span className="text-sm font-normal text-emerald-600">
                    /{realtimeStats?.total_attendees || 0}
                  </span>
                </p>
                <p className="text-xs text-emerald-600">Checked In</p>
              </div>
            </div>
            <div className="mt-2 h-1.5 bg-emerald-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${realtimeStats?.check_in_rate || 0}%` }}
              />
            </div>
            <p className="text-xs text-emerald-600 mt-1">{realtimeStats?.check_in_rate || 0}% check-in rate</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{realtimeStats?.scans_today || 0}</p>
                <p className="text-xs text-blue-600">Scans Today</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{realtimeStats?.scans_last_hour || 0}</p>
                <p className="text-xs text-purple-600">Last Hour</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Zap className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">{realtimeStats?.scans_per_minute || 0}</p>
                <p className="text-xs text-orange-600">Scans/Min</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Hourly Scans Chart */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                  Scans Per Hour
                </CardTitle>
                <CardDescription>Hourly distribution for selected date</CardDescription>
              </div>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40"
              />
            </div>
          </CardHeader>
          <CardContent>
            {scansPerHour.length > 0 ? (
              <SimpleBarChart data={scansPerHour} />
            ) : (
              <div className="h-[200px] flex items-center justify-center text-gray-400">
                No scan data for this date
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scanner Performance */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              Scanner Performance
            </CardTitle>
            <CardDescription>Scans by scanner booth</CardDescription>
          </CardHeader>
          <CardContent>
            {scansByScanner.length > 0 ? (
              <HorizontalBarChart data={scansByScanner.slice(0, 6)} />
            ) : (
              <div className="h-[150px] flex items-center justify-center text-gray-400">
                No scanner data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Scan Status Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="h-5 w-5 text-purple-500" />
              Scan Status
            </CardTitle>
            <CardDescription>Breakdown by outcome</CardDescription>
          </CardHeader>
          <CardContent>
            {statusBreakdown ? (
              <DonutChart data={statusBreakdown} />
            ) : (
              <div className="h-[200px] flex items-center justify-center text-gray-400">
                No data
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Check-in Rates */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-teal-500" />
              Category Check-in
            </CardTitle>
            <CardDescription>Check-in rate per category</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {categoryRates.length > 0 ? (
                categoryRates.map((cat, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: cat.category_color }}
                        />
                        {cat.category_name}
                      </span>
                      <span className="text-gray-500">
                        {cat.checked_in}/{cat.total} ({cat.rate}%)
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${cat.rate}%`,
                          backgroundColor: cat.category_color
                        }}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center text-gray-400">No categories</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Live Feed */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 text-red-500" />
              Live Feed
              <span className="relative flex h-2 w-2 ml-1">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
            </CardTitle>
            <CardDescription>Recent scans</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {recentScans.length > 0 ? (
                recentScans.map((scan, idx) => (
                  <LiveFeedItem key={scan.scan_id || idx} scan={scan} />
                ))
              ) : (
                <div className="py-8 text-center text-gray-400">No recent scans</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Busiest Scanner Highlight */}
      {realtimeStats?.busiest_scanner && (
        <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-xl">
                <Zap className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-amber-600 font-medium">Busiest Scanner Today</p>
                <p className="text-xl font-bold text-amber-800">
                  {realtimeStats.busiest_scanner.name}
                </p>
                <p className="text-sm text-amber-600">
                  {realtimeStats.busiest_scanner.count} scans today
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
};

export default ScannerAnalytics;
